import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useBreadcrumbs } from '../../../layout/breadcrumbsContext/useBreadcrumbs/useBreadcrumbs'
import { ReportCard, reportsBasePath } from '../../../reports/reportsConfig'

import { withCandidateStateLabels } from './candidateStateInsightsService'
import { DateBoundsFilter } from './DateBoundsFilter'
import { useCandidateStateLabels } from './useCandidateStateLabels'
import {
  DatasetColumnConfig,
  DynamicColumnsOption,
  DynamicColumnsPart,
  FilterValues,
  Insights,
  ResolvedFilter,
  toCsvFileName
} from '@percent/domain/insights'
import { Flex, Grid, Heading, Icon, Loader, Text } from '@percent/lemonade'
import { useServices } from '@percent/partner-dashboard/context/serviceContext/ServiceContext'

type CandidatesReportProps = {
  report: ReportCard
}

const EXPORT_EXCLUDED_COLUMNS = ['candidateStateId']

const DATE_BOUNDS: Record<string, { toKey: string; labelKey: 'verificationCompletedAt' | 'paymentDate' }> = {
  verificationCompletedFrom: { toKey: 'verificationCompletedTo', labelKey: 'verificationCompletedAt' },
  paymentDateFrom: { toKey: 'paymentDateTo', labelKey: 'paymentDate' }
}

/**
 * The grants Candidates report — every candidate across the partner's grant programmes.
 *
 * A candidate's state is filtered and grouped on its raw id, because each programme names its
 * own states. The report resolves those names from the programmes' state machines and wraps the
 * insights service, so the state picker and the by-state chart speak in names while the API
 * keeps working on ids. The table and the CSV are labelled by the backend.
 */
export function CandidatesReport({ report }: Readonly<CandidatesReportProps>) {
  const { t } = useTranslation()
  const { insightsService, grantsService } = useServices()
  const { labels, isLoading } = useCandidateStateLabels()

  const heading = t(report.titleKey)
  const subheading = t(report.descriptionKey)

  useBreadcrumbs([{ label: t('breadcrumb.reports'), path: reportsBasePath('grants') }, { label: heading }])

  const service = useMemo(
    () => withCandidateStateLabels({ ...insightsService, exportDataset: insightsService.exportDatasetByPost }, labels),
    [insightsService, labels]
  )

  const emptyState = useMemo(
    () => (
      <>
        <Icon name="empty" size={6} color="goodstack" />
        <Text size="small">{t('typography.dashboard.noDataToShow')}</Text>
      </>
    ),
    [t]
  )
  const errorLabel = t('typography.dashboard.failedToLoad')

  const datasetColumns = useMemo<DatasetColumnConfig[]>(
    () => [
      { id: 'organisationName', isSticky: true, width: '220px' },
      { id: 'organisationCountry' },
      { id: 'registryId' },
      { id: 'programName', width: '220px' },
      { id: 'candidateId', width: '260px' },
      { id: 'candidateState' },
      { id: 'createdAt' },
      { id: 'verificationStartedAt' },
      { id: 'verificationCompletedAt' },
      { id: 'verificationStatus' },
      { id: 'orgSignedUpAt' },
      { id: 'signUpVerification' },
      { id: 'signUpVerifiedAt' },
      { id: 'orgTasksCreatedAt' },
      { id: 'outstandingOrgTasks' },
      { id: 'outstandingReviewTasks' },
      { id: 'paymentStatus' },
      { id: 'designatedAward' },
      { id: 'designatedCurrency' },
      { id: 'paidAward' },
      { id: 'paidCurrency' },
      { id: 'paymentDate' }
    ],
    []
  )

  const dateRangeLabels = {
    custom: t('typography.dashboard.filters.preset.custom'),
    apply: t('typography.dashboard.filters.apply'),
    clear: t('typography.dashboard.filters.clear')
  }

  const filterLabels = {
    reset: t('typography.dashboard.filters.reset'),
    all: t('typography.dashboard.filters.all'),
    optionsError: t('typography.dashboard.filters.optionsLoadError'),
    dateRange: { ...dateRangeLabels, label: t('reports.candidates.filters.candidateCreatedAt') }
  }

  const visibleFilters = [
    'programNames',
    'organisationCountries',
    'candidateStates',
    'rejectionReasons',
    'verificationStatuses',
    'signUpVerifications',
    'paymentStatuses',
    'dateRange',
    'verificationCompletedFrom',
    'paymentDateFrom'
  ]

  const renderControl = (def: ResolvedFilter) => {
    const bounds = DATE_BOUNDS[def.key]

    if (!bounds) return null

    return (
      <DateBoundsFilter
        def={def}
        fromKey={def.key}
        toKey={bounds.toKey}
        labels={{ ...dateRangeLabels, label: t(`reports.candidates.filters.${bounds.labelKey}`) }}
        testId={`report-filters-${def.key}`}
      />
    )
  }

  const formSubmissionsColumns: DynamicColumnsOption = {
    group: 'form-submissions',
    label: t('reports.candidates.formSubmissions'),
    unavailable: t('reports.candidates.formSubmissionsNeedOneProgram'),
    isAvailable: ({ programNames }) => (Array.isArray(programNames) ? programNames.length === 1 : Boolean(programNames))
  }

  const loadFormTemplates = async ({ programNames }: FilterValues): Promise<DynamicColumnsPart[]> => {
    const programName = Array.isArray(programNames) ? programNames[0] : programNames
    const programs = (await grantsService.getPrograms()).data.data.filter(program => program.name === programName)

    if (programs.length !== 1) return []

    const templates = (await grantsService.getAllFormQuestions({ programId: programs[0].id })).data.data

    return templates.toReversed().map(({ taskTemplate, questions }) => ({
      id: taskTemplate.id,
      label: taskTemplate.name,
      items: questions.map(question => ({ id: question.id, label: question.dashboardLabel ?? question.label }))
    }))
  }

  const exportFormSubmissionsColumns: DynamicColumnsOption = {
    ...formSubmissionsColumns,
    parts: {
      label: t('reports.candidates.formSubmissionsTemplates'),
      selectAllLabel: t('reports.candidates.formSubmissionsSelectAll'),
      load: loadFormTemplates
    }
  }

  const exportLabels = {
    trigger: t('reports.export.trigger'),
    title: t('reports.export.title', { report: heading }),
    description: t('reports.export.description'),
    appliedFiltersHeading: t('reports.export.appliedFilters'),
    noFiltersApplied: t('reports.export.noFilters'),
    columnsLabel: t('reports.export.columns'),
    modeDefault: (count: number) => t('reports.export.mode.default', { count }),
    modeAll: (count: number) => t('reports.export.mode.all', { count }),
    modeCustom: t('reports.export.mode.custom'),
    includes: t('reports.export.includes'),
    zeroColumnsError: t('reports.export.zeroColumns'),
    cancel: t('button.cancel'),
    export: t('reports.export.confirm'),
    exportSuccess: (rowCount: number) => t('reports.export.success', { count: rowCount }),
    exportError: t('reports.export.error'),
    exportTooLarge: t('reports.export.tooLarge'),
    exportDateRangeTooLarge: t('reports.export.dateRangeTooLarge'),
    exportUnavailable: t('reports.export.unavailable'),
    close: t('button.close'),
    emptyTitle: t('reports.export.empty.title'),
    emptyFilteredDescription: t('reports.export.empty.filtered'),
    emptyNoDataDescription: t('reports.export.empty.noData')
  }

  // The state names must be known before the domain loads its filters, or the state picker
  // would be built from the raw ids and cached that way.
  if (isLoading) return <Loader />

  return (
    <Insights.Domain domain={report.domain} service={service}>
      <Flex
        direction="column"
        gap={24}
        w="100%"
        p={24}
        bg="white"
        style={{ borderRadius: 8 }}
        data-testid="report-detail"
      >
        <Flex direction="column" gap="xs">
          <Heading level="h2">{heading}</Heading>
          <Text size="small">{subheading}</Text>
        </Flex>

        <Insights.Filters
          testId="report-filters"
          labels={filterLabels}
          visibleFilters={visibleFilters}
          dropdownAlign="start"
          dateRangeOptions={{ showAllTime: true, labelWhenAllTime: true }}
          renderControl={renderControl}
        />

        <Grid gutter={16}>
          <Grid.Col span={3}>
            <Insights.Stat metric="candidate-count" errorLabel={errorLabel} testId="report-stat-candidate-count" />
          </Grid.Col>
          <Grid.Col span={3}>
            <Insights.Stat metric="country-count" errorLabel={errorLabel} testId="report-stat-country-count" />
          </Grid.Col>
          <Grid.Col span={3}>
            <Insights.Stat
              metric="orgs-signed-up-count"
              errorLabel={errorLabel}
              testId="report-stat-orgs-signed-up-count"
            />
          </Grid.Col>
          <Grid.Col span={3}>
            <Insights.Stat
              metric="orgs-onboarded-count"
              errorLabel={errorLabel}
              testId="report-stat-orgs-onboarded-count"
            />
          </Grid.Col>
          <Grid.Col span={3}>
            <Insights.Stat
              metric="outstanding-review-count"
              errorLabel={errorLabel}
              testId="report-stat-outstanding-review-count"
            />
          </Grid.Col>
          <Grid.Col span={3}>
            <Insights.Stat metric="paid-count" errorLabel={errorLabel} testId="report-stat-paid-count" />
          </Grid.Col>
          <Grid.Col span={3}>
            <Insights.Stat metric="awarded-total" errorLabel={errorLabel} testId="report-stat-awarded-total" />
          </Grid.Col>
        </Grid>

        <Insights.BarChart
          metric="by-state"
          orientation="horizontal"
          yAxisWidth={220}
          rowLabels={labels.labelById}
          emptyState={emptyState}
          errorLabel={errorLabel}
          testId="report-chart-by-state"
        />

        <Insights.BarChart
          metric="by-program"
          orientation="horizontal"
          yAxisWidth={220}
          emptyState={emptyState}
          errorLabel={errorLabel}
          testId="report-chart-by-program"
        />

        <Insights.DonutChart
          metric="by-country"
          showLegend
          emptyState={emptyState}
          errorLabel={errorLabel}
          testId="report-chart-by-country"
        />

        <Insights.Dataset
          dataset="candidates"
          columns={datasetColumns}
          dynamicColumns={formSubmissionsColumns}
          title={t('reports.candidates.table.title')}
          action={
            <Insights.Export
              dataset="candidates"
              defaultColumns={datasetColumns}
              excludeColumns={EXPORT_EXCLUDED_COLUMNS}
              dynamicColumns={exportFormSubmissionsColumns}
              fileName={toCsvFileName(heading)}
              labels={exportLabels}
              testId="report-export"
            />
          }
          pageSizeSelector={{ options: [10, 20, 50], label: t('typography.resultsOnPage') }}
          labels={{
            empty: t('reports.candidates.table.empty'),
            error: errorLabel,
            results: count => t('reports.candidates.table.results', { count })
          }}
          testId="report-dataset"
        />
      </Flex>
    </Insights.Domain>
  )
}
